import { useEffect, InputHTMLAttributes } from "react"
import { useForm } from "./Root"
import { FieldValidator } from "./types"

type GetInputPropsFn = (inputProps?: InputHTMLAttributes<HTMLInputElement>) => InputHTMLAttributes<HTMLInputElement>

type FieldRenderProps = Readonly<{
  error?: string
  getInputProps: GetInputPropsFn
}>

export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "children"> &
  Readonly<{
    field: string
    children: (renderProps: FieldRenderProps) => React.ReactNode | React.ReactNode[]
    validate?: FieldValidator
    parse?: () => void
  }>

export const Field = ({ field, children, validate, parse, ...globalInputProps }: FieldProps) => {
  const { state, dispatch } = useForm(`Field`)

  useEffect(() => {
    dispatch({
      type: `setFormRecord`,
      field,
      record: {
        validator: validate,
      },
    })
  }, [field]) // @ToDo dispatch and validate should be dependencies here, but they are causing max update depth to be exceeded

  const onBlur =
    (inputProps?: React.AllHTMLAttributes<HTMLInputElement>) => (event: React.FocusEvent<HTMLInputElement>) => {
      const error = validate ? validate(state.values[field]?.value) : undefined

      dispatch({
        type: `setFormRecord`,
        field,
        record: {
          value: event.target.value,
          error,
          validator: validate,
        },
      })

      if (inputProps?.onBlur) {
        inputProps.onBlur(event)
      }
    }

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: `setFormRecord`,
      field,
      record: {
        value: event.target.value,
        validator: validate,
      },
    })
  }

  const getInputProps: GetInputPropsFn = (inputProps) => ({
    ...globalInputProps,
    ...inputProps,
    value: state.values[field]?.value ?? ``,
    onBlur: onBlur(inputProps),
    onChange,
  })

  return (
    <>
      {children({
        getInputProps,
        error: state.values[field]?.error,
      })}
    </>
  )
}
